import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

/**
 * Modal Component - Système de modal moderne avec React Portal
 * 
 * @param {boolean} isOpen - Afficher ou non le modal
 * @param {function} onClose - Callback appelé lors de la fermeture (backdrop click, ESC)
 * @param {string} title - Titre du modal (accessibilité)
 * @param {boolean} closeOnBackdrop - Fermer en cliquant sur le backdrop (défaut: true)
 * @param {boolean} closeOnESC - Fermer avec la touche ESC (défaut: true)
 * @param {string} size - Taille du modal: 'small', 'medium', 'large' (défaut: 'medium')
 * @param {ReactNode} children - Contenu du modal
 * @returns {JSX.Element|null}
 */
export const Modal = ({
    isOpen,
    onClose,
    title,
    closeOnBackdrop = true,
    closeOnESC = true,
    size = 'medium',
    children
}) => {
    const modalRef = useRef(null);
    const previousActiveElement = useRef(null);

    const handleKeyDown = useCallback((event) => {
        if (closeOnESC && event.key === 'Escape') {
            onClose();
        }
        
        if (event.key === 'Tab' && modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement?.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement?.focus();
            }
        }
    }, [closeOnESC, onClose]);

    const handleBackdropClick = useCallback((event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
            onClose();
        }
    }, [closeOnBackdrop, onClose]);

    useEffect(() => {
        if (isOpen) {
            previousActiveElement.current = document.activeElement;
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            
            setTimeout(() => {
                const firstFocusable = modalRef.current?.querySelector(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                (firstFocusable || modalRef.current)?.focus();
            }, 0);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
            previousActiveElement.current?.focus();
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const modalContent = (
        <div 
            className="modal-backdrop"
            onClick={handleBackdropClick}
            role="presentation"
        >
            <div
                ref={modalRef}
                className={`modal-content modal-${size}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'modal-title' : undefined}
                onClick={(e) => e.stopPropagation()}
            >
                {title && (
                    <div className="modal-header">
                        <h2 id="modal-title" className="modal-title">{title}</h2>
                        <button
                            className="modal-close"
                            onClick={onClose}
                            aria-label="Fermer"
                            type="button"
                        >
                            ✕
                        </button>
                    </div>
                )}
                <div className="modal-body">
                    {children}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

/**
 * Modal Footer Component - Pour les boutons d'action
 */
export const ModalFooter = ({ children, className = '' }) => (
    <div className={`modal-footer ${className}`.trim()}>
        {children}
    </div>
);

/**
 * Confirm Dialog Component - Dialogue de confirmation réutilisable
 */
export const ConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Oui',
    cancelText = 'Non',
    confirmVariant = 'primary'
}) => {
    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="small"
        >
            <p className="confirm-message">{message}</p>
            <ModalFooter>
                <button
                    className={`modal-btn modal-btn-${confirmVariant}`}
                    onClick={handleConfirm}
                    type="button"
                >
                    {confirmText}
                </button>
                <button
                    className="modal-btn modal-btn-cancel"
                    onClick={onClose}
                    type="button"
                >
                    {cancelText}
                </button>
            </ModalFooter>
        </Modal>
    );
};
